import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Buildings, ArrowRight, Plus, ShieldCheck, CloudArrowUp, Database, ArrowsLeftRight, Gear, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * v1.25.6 — Super User Dashboard. Landing screen for indiamdimpex@gmail.com.
 * Lists every company. Clicking a company sets the impersonation header and
 * routes into that company's normal admin dashboard.
 */
export default function SuperDashboard() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Clear any prior impersonation so the SU sees the true list
        localStorage.removeItem("micm_impersonate_company");
        api.get("/super/companies")
            .then((r) => setCompanies(r.data.companies || []))
            .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load companies"))
            .finally(() => setLoading(false));
    }, []);

    const enterCompany = (c) => {
        localStorage.setItem("micm_impersonate_company", c.id);
        // v1.28.96 — In-app nav preserves the impersonation cookie/header
        // and lets React re-scope every fetch on the route change. Was:
        // window.location.assign("/admin") — a hard reload that could race
        // the 401 interceptor and wipe the just-set impersonation.
        window.dispatchEvent(new CustomEvent("micm-impersonation-changed", { detail: { company_id: c.id } }));
        navigate("/admin", { replace: true });
    };

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-8" data-testid="super-dashboard">
            <div className="mx-auto max-w-6xl">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700">
                            <ShieldCheck size={14} weight="fill" /> Super User
                        </div>
                        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900 sm:text-4xl">Company Dashboard</h1>
                        <p className="mt-2 text-sm text-slate-500">Pick a company to open its full workspace. Each is completely isolated — your changes only affect the tenant you're viewing.</p>
                    </div>
                    <Button data-testid="new-company-btn" onClick={() => navigate("/admin/super/companies")} className="bg-violet-700 text-white hover:bg-violet-800">
                        <Plus size={14} className="mr-1.5" /> Manage Companies
                    </Button>
                </div>

                {/* v1.27.1 — Super User Tools · quick-access to admin/settings screens that live outside a company workspace */}
                <div className="mb-8" data-testid="su-tools-section">
                    <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                        <Gear size={12} weight="fill" /> Administration &amp; Settings
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                        {[
                            { key: "companies",     to: "/admin/super/companies",     label: "Manage Companies", desc: "Create · rename · activate", Icon: Buildings,       tone: "from-violet-500 to-fuchsia-600" },
                            { key: "backup",        to: "/admin/super/backup-center", label: "Backup Center",    desc: "Download & restore",          Icon: Database,        tone: "from-emerald-500 to-teal-600" },
                            { key: "daily-archive", to: "/admin/super/daily-archive", label: "Daily G-Drive Archive", desc: "OAuth · folder · nightly PDFs", Icon: CloudArrowUp,   tone: "from-sky-500 to-indigo-600" },
                            { key: "transfer",      to: "/admin/super/transfer-party",label: "Transfer Party",   desc: "Move party between tenants",  Icon: ArrowsLeftRight, tone: "from-amber-500 to-orange-600" },
                            { key: "trace-utr",     to: "/admin/super/trace-utr",     label: "Trace UTR",        desc: "Diagnose · fix orphans",      Icon: MagnifyingGlass, tone: "from-rose-500 to-red-600" },
                        ].map(({ key, to, label, desc, Icon, tone }) => (
                            <button
                                key={key}
                                type="button"
                                data-testid={`su-tool-${key}`}
                                onClick={() => navigate(to)}
                                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                            >
                                <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${tone} text-white shadow-sm`}>
                                    <Icon size={20} weight="duotone" />
                                </div>
                                <div className="mt-3 font-display text-sm font-bold text-slate-900">{label}</div>
                                <div className="mt-0.5 text-[11px] text-slate-500">{desc}</div>
                                <ArrowRight size={12} weight="bold" className="absolute right-3 top-3 text-slate-300 transition-all group-hover:right-2 group-hover:text-indigo-500" />
                            </button>
                        ))}
                    </div>
                </div>

                {loading && <div className="text-sm text-slate-500">Loading…</div>}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {companies.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            data-testid={`enter-company-${c.id}`}
                            onClick={() => enterCompany(c)}
                            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-indigo-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${c.active ? "bg-gradient-to-br from-indigo-500 to-violet-600 text-white" : "bg-slate-200 text-slate-400"}`}>
                                    <Buildings size={22} weight="duotone" />
                                </div>
                                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"}`}>
                                    {c.active ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="mt-4 font-display text-xl font-bold text-slate-900">{c.name}</div>
                            <div className="text-[11px] text-slate-500">Code {c.code || "—"}</div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                                <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Parties</div><div className="font-display text-lg font-bold text-slate-800 tabular-nums">{c.parties_count}</div></div>
                                <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Uploads</div><div className="font-display text-lg font-bold text-slate-800 tabular-nums">{c.uploads_count}</div></div>
                                <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Users</div><div className="font-display text-lg font-bold text-slate-800 tabular-nums">{c.users_count}</div></div>
                            </div>
                            <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 opacity-0 transition-opacity group-hover:opacity-100">
                                Enter workspace <ArrowRight size={12} weight="bold" />
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
